import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Theme from "./Theme.js";
import IconUtil from "./IconUtil.js";
import Button from "./Button.js";
import S from './CompositeTextInput.css';
/**
 * The CompositeTextInput renders a TextInput component inline with optional accompanying icons, buttons and pre and post components
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
class CompositeTextInput extends React.Component {
  static contextType = Theme.Context;

  /* eslint-disable react/require-default-props */
  static propTypes = {
    /**
     * Function to be called when the checkbox's value is changed
     */
    onChange: PropTypes.func,
    /**
     * Function to be called when the text input gains focus
     */
    onFocus: PropTypes.func,
    /**
     * A validator function to be called when the component's value changes.
     * Return false to prevent the value from changing and true to allow.
     */
    validator: PropTypes.func,
    /**
     * Function to call on key press which returns true if the keypress should be accepted and 
     * false if not
     */
    acceptKey: PropTypes.func,
    /**
     * Whether the onChange event should fire on the use of the Enter key
     */
    changeOnReturn: PropTypes.bool,
    /**
     * Whether the onChange event should fire onBlur
     */
    changeOnBlur: PropTypes.bool,
    /**
     * Not clear how this is any different from changeOnReturn
     */
    alwaysFireOnReturn: PropTypes.bool,
    /**
     * Not clear how this is any different from changeOnBlur
     */
    alwaysFireOnBlur: PropTypes.bool,
    /**
     * Whether to always place the input caret at the end of the value text when focusing the element
     */
    placeCaretAtEnd: PropTypes.bool,
    /**
     * Whether the element should be focused immediately on mount
     */
    focus: PropTypes.bool,
    /**
     * Whether the element should be disable
     */
    disabled: PropTypes.bool,
    /**
     * Placeholder text for the input
     */
    hint: PropTypes.string,
    /**
     * The value to be bound to the text input's value attribute
     */
    value: PropTypes.string,
    /**
     * The HTML maxlength property for the text input
     */
    maxLength: PropTypes.number,
    /**
     * The HTML size property for the text input
     */
    size: PropTypes.number,
    /**
     * The component to be rendered before the TextInput component
     */
    pre: PropTypes.node,
    /**
     * Whether to render the pre component before or after the icon
     */
    preLocation: PropTypes.oneOf(['preIcon', 'postIcon']),
    /**
     * The component to be rendered after the TextInput component
     */
    post: PropTypes.node,
    /**
     * Whether to render the post component before or after the Button component
     */
    postLocation: PropTypes.oneOf(['preButton', 'postButton']),
    /**
     * Props to pass to the Button component
     */
    buttonProps: PropTypes.object,
    /**
     * Theme variant to be passed to the Button component
     */
    buttonVariant: PropTypes.string,
    /**
     * CSS class to add to the TextInput component
     */
    inputClassName: PropTypes.string,
    /**
     * CSS class to add to the Button component
     */
    buttonClassName: PropTypes.string,
    /**
     * Icon to be rendered before the TextInput component
     */
    icon: PropTypes.object,
    /**
     * Icon rendering options
     */
    iconOpts: PropTypes.object,
    /**
     * Image rendering options
     */
    imageOpts: PropTypes.any,
    /**
     * Theme variant for this component
     */
    variant: PropTypes.string,
    /**
     * Event data to be passed as the data property to the onChange function
     */
    eventData: PropTypes.any
  };
  /* eslint-enable react/require-default-props */

  static defaultProps = {
    changeOnReturn: true,
    changeOnBlur: true,
    alwaysFireOnReturn: false,
    acceptKey: null,
    validator: null
  };
  constructor(props) {
    super(props);
    const {
      value
    } = this.props;
    const val = value || '';
    this.state = {
      value: val,
      error: this.isInError(val)
    };
  }
  componentDidUpdate(prevProps) {
    const {
      value
    } = this.props;
    if (value !== prevProps.value) {
      this.setState({
        value,
        error: this.isInError(value)
      });
    }
  }
  componentDidMount() {
    const {
      focus
    } = this.props;
    if (focus && this.input) {
      setTimeout(() => this.input && this.input.focus(), 1);
    }
  }
  isInError(value) {
    const {
      validator
    } = this.props;
    if (validator) {
      return !validator(value);
    }
    return false;
  }
  onChange = e => {
    const error = this.isInError(e.target.value);
    const {
      changeOnReturn,
      alwaysFireOnReturn,
      onChange,
      eventData
    } = this.props;
    this.setState({
      value: e.target.value,
      error
    });
    if (changeOnReturn === false && alwaysFireOnReturn === false && onChange) {
      onChange({
        value: e.target.value,
        error,
        data: eventData
      });
    }
  };
  onFocus = e => {
    const {
      placeCaretAtEnd,
      onFocus
    } = this.props;
    if (placeCaretAtEnd) {
      const dom = this.input;
      setTimeout(() => {
        const index = dom.value ? dom.value.length : 0;
        dom.setSelectionRange(index, index);
      }, 0);
    }
    if (onFocus) {
      onFocus(e);
    }
  };
  onKeyPress = e => {
    const {
      alwaysFireOnReturn,
      onChange,
      changeOnReturn,
      value,
      eventData,
      allowError,
      acceptKey
    } = this.props;
    const {
      error
    } = this.state;
    const rtn = e.which === 13 || e.keyCode === 13;
    if (rtn && (changeOnReturn || alwaysFireOnReturn)) {
      if (onChange && alwaysFireOnReturn) {
        return onChange({
          event: e,
          isReturn: rtn,
          value: e.target.value,
          error,
          data: eventData
        });
      }
      if (e.target.value === value) {
        return true;
      }
      const err = this.isInError(e.target.value);
      if (err && allowError !== true) {
        return true;
      }
      if (onChange) {
        onChange({
          event: e,
          isReturn: rtn,
          value: e.target.value,
          error,
          data: eventData
        });
      }
      return true;
    }
    if (acceptKey && acceptKey(e) === false) {
      e.preventDefault();
      return false;
    }
    return true;
  };
  onBlur = e => {
    const val = e.target.value;
    const {
      value,
      onChange,
      allowError,
      changeOnBlur,
      alwaysFireOnBlur,
      eventData,
      onBlur
    } = this.props;
    const {
      error
    } = this.state;
    if (error) {
      // Return the value
      const err = this.isInError(val);
      if (allowError !== true && err) {
        // We are leaving the input. reset to last value if we dont allow errors
        e.target.value = value;
        this.setState({
          value,
          error: this.isInError(value)
        });
      } else {
        const same = alwaysFireOnBlur ? false : value == null ? val == null || val.trim() === '' : value === val;
        if (changeOnBlur && onChange && !same) {
          onChange({
            value: val,
            error: true,
            data: eventData,
            causedByBlur: true
          });
        }
      }
    } else {
      const same = alwaysFireOnBlur ? false : value == null ? val == null || val.trim() === '' : value === val;
      if (changeOnBlur && onChange && !same) {
        onChange({
          value: val,
          error: false,
          data: eventData,
          causedByBlur: true
        });
      }
    }
    if (onBlur && typeof onBlur === 'function') {
      onBlur(e);
    }
  };
  focusInput = evt => {
    if (evt.target === this.container) {
      return this.input?.focus();
    }
    return null;
  };
  inputRef = val => {
    const {
      domRef
    } = this.props;
    this.input = val;
    if (domRef) domRef.current = val;
  };
  containerRef = val => {
    const {
      containerRef
    } = this.props;
    this.container = val;
    if (containerRef) containerRef.current = val;
  };
  render() {
    const theme = this.context;
    /* eslint-disable no-unused-vars */
    const {
      className,
      variant,
      id,
      autoFocus,
      hint,
      placeholder,
      maxLength,
      size = '10',
      value,
      style,
      eventData,
      focus,
      placeCaretAtEnd,
      changeOnBlur,
      changeOnReturn,
      alwaysFireOnReturn,
      allowError,
      onChange,
      onKeyDown,
      onKeyPress,
      onFocus,
      onBlur,
      acceptKey,
      validator,
      password = false,
      disabled,
      layoutOptions,
      domRef,
      alwaysFireOnBlur,
      pre,
      preLocation,
      post,
      postLocation,
      buttonVariant,
      buttonProps,
      inputClassName,
      buttonClassName,
      iconOpts: pIconOpts,
      imageOpts: pImageOpts,
      passFocusTo,
      containerRef,
      error: pError,
      ...rest
    } = this.props;
    const {
      icon,
      imageOpts,
      iconOpts,
      button
    } = theme.props('CompositeTextInput', this.props);
    const {
      error,
      value: stateValue
    } = this.state;
    /* eslint-enable no-unused-vars */
    const cn = theme.classes('CompositeTextInput', 'compositeTextInput', variant, className);
    const inputCn = theme.subclasses('CompositeTextInput', 'input', inputClassName);
    let err = false;
    if (pError != null) {
      err = !!pError;
    } else if (this.state == null || error == null) {
      err = this.isInError(value);
    } else {
      err = this.state && error;
    }
    const attr = err && {
      'data-error': err
    };

    // icon|pre|input|post|button

    let iconCmp = null;
    if (icon && icon !== '') {
      iconCmp = IconUtil.create(icon, imageOpts, iconOpts, theme);
    }
    let buttonCmp = null;
    if (button || buttonProps) {
      const buttonCn = theme.subclasses('CompositeTextInput', 'button', buttonClassName);
      buttonCmp = /*#__PURE__*/_jsx(Button, {
        variant: buttonVariant,
        disabled: disabled,
        className: buttonCn,
        ...buttonProps,
        children: button
      });
    }
    return /*#__PURE__*/_jsxs("div", {
      tabIndex: passFocusTo != null ? 0 : null,
      onFocus: passFocusTo === 'input' ? this.focusInput : null,
      ref: this.containerRef,
      "data-disabled": disabled,
      className: classnames(cn, S.container),
      ...attr,
      children: [preLocation === 'preIcon' ? pre : null, iconCmp, preLocation !== 'preIcon' ? pre : null, /*#__PURE__*/_jsx("input", {
        className: inputCn,
        id: id,
        type: password ? 'password' : 'text',
        disabled: disabled,
        autoFocus: autoFocus === true,
        ref: this.inputRef,
        value: stateValue != null ? stateValue : '',
        placeholder: hint || '',
        maxLength: maxLength,
        size: size,
        style: style,
        ...rest,
        onChange: this.onChange,
        onFocus: this.onFocus,
        onKeyDown: onKeyDown,
        onKeyPress: this.onKeyPress,
        onBlur: this.onBlur
      }), postLocation !== 'postButton' ? post : null, buttonCmp, postLocation === 'postButton' ? post : null]
    });
  }
}
export default CompositeTextInput;